import styled from "styled-components"

import { colors, spacing, Text } from "@minuthq/meatball-ui-react"

import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { getIntegrationsCoverageTotals } from "src/data/portfolioAnalytics/logic/portfolioCoverage"
import {
  useFetchDeviceStatusByOrganization,
  useFetchIntegrationsCoveragePerIntegration,
  useFetchWifiHealthByOrganization,
} from "src/data/portfolioAnalytics/portfolioAnalyticsQueries"
import {
  TDeviceStatusAnalytics,
  TDeviceStatusAnalyticsData,
  TDeviceWifiHealthAnalytics,
  TWifiHealthAnalyticsData,
} from "src/data/portfolioAnalytics/types/portfolioAnalyticsTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { MCard } from "src/ui/MCard/MCard"
import { getPercentage } from "src/utils/genericUtil"

export function PortfolioAnalyticsSummaryCard() {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()

  const fetchDeviceStatusByOrganization = useFetchDeviceStatusByOrganization({
    orgId,
  })

  const fetchWifiHealthByOrganization = useFetchWifiHealthByOrganization({
    orgId,
  })

  const fetchIntegrationsCoveragePerIntegration =
    useFetchIntegrationsCoveragePerIntegration({
      orgId,
    })

  const analytics = fetchDeviceStatusByOrganization.data?.analytics
  const wifiAnalytics = fetchWifiHealthByOrganization.data?.analytics
  const integrationsAnalytics =
    fetchIntegrationsCoveragePerIntegration.data?.analytics

  const unmonitoredUnits =
    analytics?.reduce(
      (total: number, group: TDeviceStatusAnalytics) =>
        total +
        group.data.reduce(
          (groupTotal: number, item: TDeviceStatusAnalyticsData) =>
            groupTotal +
            item.aggregation.all_offline +
            item.aggregation.no_devices,
          0
        ),
      0
    ) ?? "-"

  const unitsWithPoorWifi =
    wifiAnalytics?.reduce(
      (total: number, group: TDeviceWifiHealthAnalytics) =>
        total +
        group.data.reduce(
          (groupTotal: number, item: TWifiHealthAnalyticsData) =>
            groupTotal + item.aggregation.poor + item.aggregation.none,
          0
        ),
      0
    ) ?? "-"

  const { totalUnits, linkedUnits } = integrationsAnalytics
    ? getIntegrationsCoverageTotals(integrationsAnalytics)
    : { totalUnits: 0, linkedUnits: 0 }

  return (
    <MCard boxShadow={false} border={true} padding={spacing.L}>
      <Text variant="heading3" color="secondary">
        {t(langKeys.summary)}
      </Text>
      <Summary>
        <SummaryReading>
          <Label>
            <SensorValueLabel variant="heading2" color="emergency">
              {unmonitoredUnits}
            </SensorValueLabel>
            <Text variant="bodyS" color="secondary">
              {t(langKeys.portfolio_analytics_unmonitored)}
            </Text>
          </Label>
        </SummaryReading>

        <SummaryReading>
          <Label>
            <SensorValueLabel variant="heading2" color="warning">
              {unitsWithPoorWifi}
            </SensorValueLabel>
            <Text variant="bodyS" color="secondary">
              {t(langKeys.units_with_poor_or_no_wifi)}
            </Text>
          </Label>
        </SummaryReading>

        <SummaryReading>
          <Label>
            <SensorValueLabel variant="heading2" color="primary">
              {getPercentage(linkedUnits, totalUnits)}%
            </SensorValueLabel>
            <Text variant="bodyS" color="secondary">
              {t(langKeys.portfolio_analytics_totals_units_using_minut)}
            </Text>
          </Label>
        </SummaryReading>
      </Summary>
    </MCard>
  )
}

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${spacing.M};

  @media (min-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`

const SummaryReading = styled.div`
  flex: 1;
  padding: ${spacing.M} 0;
  border: 1px solid ${colors.divider};
  border-width: 0 0 1px 0;
  white-space: nowrap;
  min-width: 200px;

  &:last-child {
    border-width: 0;
  }

  @media (min-width: 768px) {
    padding: 0 ${spacing.M};
    border-width: 0 1px 0 0;

    &:first-child {
      padding: 0 ${spacing.M} 0 0;
    }

    &:last-child {
      border-width: 0;
      padding: 0 0 0 ${spacing.M};
    }
  }
`

const Label = styled.div`
  display: flex;
  flex-direction: column;
  white-space: wrap;
  gap: ${spacing.XS};
`

const SensorValueLabel = styled(Text)`
  display: flex;
  margin-top: ${spacing.M};
  gap: ${spacing.XS2};
`
